import { z } from 'zod'

/**
 * Executive Assistant delegation types — mirrors
 * services/api/src/schemas/delegation.py (the admin CRUD payload) and
 * the existing delegate-side response used by /api/v1/me/delegations.
 *
 * Two consumer surfaces:
 *   - HR admin page (full delegation row with audit pair)
 *   - Acting-as bar (flat principal fields for the picker)
 *
 * See docs/operations/ea-delegation-rfc.md for the full design.
 */

export const delegationScopeSchema = z.enum(['apply_only', 'view_only'])
export type DelegationScope = z.infer<typeof delegationScopeSchema>

export const delegationSchema = z.object({
  id: z.guid(),
  principalEmployeeId: z.guid(),
  delegateEmployeeId: z.guid(),
  scope: delegationScopeSchema,
  delegableLeaveTypes: z.array(z.string()),
  /** Subset of the principal's reports this delegation covers (alembic 0119).
   *  Empty = unscoped (legacy: covers the principal as apply-on-behalf subject). */
  scopeEmployeeIds: z.array(z.guid()).default([]),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  active: z.boolean(),
  // Display-only join fields populated by manager-side routes so the FE
  // can render a delegate name + email instead of a UUID. Default to empty
  // string for older payloads from routes that don't enrich.
  delegateName: z.string().default(''),
  delegatePreferredName: z.string().nullable().default(null),
  delegateEmail: z.string().default(''),
  // v2 approve-on-behalf fields (migration 0042 / 0061).
  // `.default()` produces a non-optional output type while accepting absent
  // values from older API payloads — z.infer<Delegation>.canApprove resolves
  // to `boolean`, not `boolean | undefined`.
  canApprove: z.boolean().default(false),
  approvableLeaveTypes: z.array(z.string()).default([]),
  approvalExpiresAt: z.string().nullable().default(null),
  createdByEmployeeId: z.guid(),
  revokedByEmployeeId: z.guid().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Delegation = z.infer<typeof delegationSchema>

export const delegationCreateRequestSchema = z.object({
  principalEmployeeId: z.guid(),
  delegateEmployeeId: z.guid(),
  scope: delegationScopeSchema.default('apply_only'),
  delegableLeaveTypes: z.array(z.string()).default(['annual']),
  /** Optional per-subordinate scope (alembic 0119). Empty / omitted = unscoped. */
  scopeEmployeeIds: z.array(z.guid()).optional(),
  startsAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
  // v2 approve-on-behalf fields (RFC v2)
  canApprove: z.boolean().default(false),
  approvableLeaveTypes: z.array(z.string()).optional(),
  approvalExpiresAt: z.string().nullable().optional(),
})
export type DelegationCreateRequest = z.infer<typeof delegationCreateRequestSchema>

/** Flat-principal shape returned by GET /api/v1/me/delegations.
 *  The picker on the acting-as bar reads this directly so it doesn't
 *  need a second employee fetch.
 *
 *  v2 fields: `canApprove` and `approvableLeaveTypes` are included so the
 *  acting-as context can derive `capability = "approve" | "apply"` without
 *  an additional fetch. The backend filters `canApprove` to `false` when
 *  `approval_expires_at` has passed, so the FE sees a clean boolean. */
export const delegationAsDelegateSchema = z.object({
  id: z.guid(),
  principalEmployeeId: z.guid(),
  principalName: z.string(),
  principalPreferredName: z.string().nullable(),
  principalEmail: z.string(),
  principalRole: z.string(),
  principalTeamId: z.guid(),
  scope: delegationScopeSchema,
  delegableLeaveTypes: z.array(z.string()),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  /** Whether the delegate has approval authority for this delegation (RFC v2). */
  canApprove: z.boolean().default(false),
  /** Subset of leave types the delegate may approve on the principal's behalf. */
  approvableLeaveTypes: z.array(z.string()).default([]),
})
export type DelegationAsDelegate = z.infer<typeof delegationAsDelegateSchema>
